import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../ui/alert-dialog';
import { Separator } from '../../ui/separator';
import { Code, Plus, FolderTree, Hash, DollarSign, BarChart3, Settings, Building, Wrench, Truck, Users, FileText, AlertCircle, CheckCircle, TrendingUp, Calculator, Eye, Edit, Trash2, Archive, Copy, MoreVertical, Search, Calendar, User, Shield, X } from 'lucide-react';
import { financialService, CostCodeItem, CostCodeCreateData } from '../../../src/services/financial.service';
import { useAuth } from '../../../src/hooks/useAuth';
import { useCostCodes } from '../../../src/hooks/queries/use-cost-codes';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { useOrgCurrency } from '../../../src/hooks/useOrgCurrency';
import { formatCurrency } from '../../../src/utils/currency';

interface CostCode {
  id: string;
  code: string;
  division: string;
  category: string;
  description: string;
  unit: string;
  laborRate?: number;
  materialRate?: number;
  equipmentRate?: number;
  totalBudget: number;
  actualCost: number;
  committed: number;
  variance: number;
  isActive: boolean;
  parentCode?: string;
  level: number;
  createdDate: string;
  lastModified: string;
  createdBy: string;
  notes?: string;
}

interface CostCodeAnalysis {
  division: string;
  totalBudget: number;
  actualCost: number;
  committed: number;
  variance: number;
  percentComplete: number;
  codes: number;
}

export function CostCodes() {
  const orgCurrency = useOrgCurrency();
  const [selectedCostCode, setSelectedCostCode] = useState<CostCode | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit' | 'add'>('view');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { orgId } = useAuth();
  const { data: costCodeList = [], isLoading: loading, refetch: refetchCostCodes } = useCostCodes(orgId);

  const handleSaveCostCode = async () => {
    if (!orgId) return;
    setIsSubmitting(true);
    try {
      const payload: CostCodeCreateData = {
        organization_id: orgId,
        code: formData.code || '',
        short_description: formData.description || '',
        level: 'detail',
        type: (formData.category?.toLowerCase() || 'other') as any,
        unit_of_measure: formData.unit || undefined,
        budgeted_amount: formData.totalBudget || undefined,
        labor_rate: formData.laborRate || undefined,
        material_rate: formData.materialRate || undefined,
        equipment_rate: formData.equipmentRate || undefined,
        notes: formData.notes || undefined,
      };
      if (selectedCostCode?.id && dialogMode === 'edit') {
        await financialService.updateCostCodeItem(selectedCostCode.id, payload);
        showSuccess('Cost code updated');
      } else {
        await financialService.createCostCodeItem(payload);
        showSuccess('Cost code created');
      }
      setIsDialogOpen(false);
      refetchCostCodes();
    } catch {
      showError('Failed to save cost code');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedCostCode?.id) return;
    try {
      await financialService.deleteCostCodeItem(selectedCostCode.id);
      showSuccess('Cost code deleted');
      setShowDeleteDialog(false);
      refetchCostCodes();
    } catch {
      showError('Failed to delete cost code');
    }
  };

  const costCodes: CostCode[] = (costCodeList as any[]).map(c => ({
    id: c.cost_code_id,
    code: c.code,
    division: c.type || 'General',
    category: c.type || '',
    description: c.short_description,
    unit: c.unit_of_measure || '',
    laborRate: c.labor_rate || 0,
    materialRate: c.material_rate || 0,
    equipmentRate: c.equipment_rate || 0,
    totalBudget: c.budgeted_amount || 0,
    actualCost: c.actual_amount || 0,
    committed: c.committed_amount || 0,
    variance: (c.budgeted_amount || 0) - (c.actual_amount || 0),
    isActive: c.status !== 'Inactive',
    parentCode: c.parent_id || undefined,
    level: 1,
    createdDate: c.created_at?.split('T')[0] || '',
    lastModified: c.updated_at?.split('T')[0] || '',
    createdBy: '',
    notes: c.notes || '',
  }));

  const divisionAnalysis: CostCodeAnalysis[] = useMemo(() => {
    const byDivision: Record<string, CostCodeAnalysis> = {};
    for (const code of costCodes) {
      const div = code.division || 'General';
      if (!byDivision[div]) byDivision[div] = { division: div, totalBudget: 0, actualCost: 0, committed: 0, variance: 0, percentComplete: 0, codes: 0 };
      byDivision[div].totalBudget += code.totalBudget;
      byDivision[div].actualCost += code.actualCost;
      byDivision[div].committed += code.committed;
      byDivision[div].variance += code.variance;
      byDivision[div].codes += 1;
    }
    return Object.values(byDivision).map(d => ({
      ...d,
      percentComplete: d.totalBudget > 0 ? Math.round((d.actualCost / d.totalBudget) * 100 * 10) / 10 : 0,
    }));
  }, [costCodes]);

  const [formData, setFormData] = useState<Partial<CostCode>>({
    code: '',
    division: '',
    category: '',
    description: '',
    unit: '',
    laborRate: 0,
    materialRate: 0,
    equipmentRate: 0,
    totalBudget: 0,
    actualCost: 0,
    committed: 0,
    variance: 0,
    isActive: true,
    parentCode: '',
    level: 1,
    createdDate: '',
    lastModified: '',
    createdBy: '',
    notes: ''
  });

  const handleOpenDialog = (mode: 'view' | 'edit' | 'add', costCode?: CostCode) => {
    setDialogMode(mode);
    if (costCode) {
      setSelectedCostCode(costCode);
      setFormData(costCode);
    } else {
      setSelectedCostCode(null);
      setFormData({
        code: '',
        division: '',
        category: '',
        description: '',
        unit: '',
        laborRate: 0,
        materialRate: 0,
        equipmentRate: 0,
        totalBudget: 0,
        actualCost: 0,
        committed: 0,
        variance: 0,
        isActive: true,
        parentCode: '',
        level: 1,
        createdDate: '',
        lastModified: '',
        createdBy: '',
        notes: ''
      });
    }
    setIsDialogOpen(true);
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? 
      <Badge className="bg-green-100 text-green-800">Active</Badge> : 
      <Badge variant="outline">Inactive</Badge>;
  };

  const getVarianceBadge = (variance: number) => {
    if (variance > 0) {
      return <Badge className="bg-green-100 text-green-800">Under Budget</Badge>;
    } else if (variance < 0) {
      return <Badge className="bg-red-100 text-red-800">Over Budget</Badge>;
    } else {
      return <Badge variant="outline">On Budget</Badge>;
    }
  };

  const getDivisionIcon = (division: string) => {
    if (division.includes('Concrete')) return <Building className="h-4 w-4" />;
    if (division.includes('Metal')) return <Wrench className="h-4 w-4" />;
    if (division.includes('Wood')) return <Building className="h-4 w-4" />;
    if (division.includes('Electrical')) return <Code className="h-4 w-4" />;
    if (division.includes('Mechanical')) return <Settings className="h-4 w-4" />;
    return <Hash className="h-4 w-4" />;
  };

  const calculateTotalRate = (code: CostCode) => {
    return (code.laborRate || 0) + (code.materialRate || 0) + (code.equipmentRate || 0);
  };

  const calculatePercentComplete = (code: CostCode) => {
    if (code.totalBudget === 0) return 0;
    return ((code.actualCost + code.committed) / code.totalBudget) * 100;
  };

  const filteredCostCodes = costCodes.filter(code => {
    const matchesSearch = code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         code.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         code.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || code.division === categoryFilter;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && code.isActive) ||
                         (statusFilter === 'inactive' && !code.isActive);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const handleApprove = (costCode: CostCode) => {
    setSelectedCostCode(costCode);
    setApprovalNotes('');
    setApprovalAction(null);
    setShowApprovalModal(true);
  };

  const handleDelete = (costCode: CostCode) => {
    setSelectedCostCode(costCode);
    setShowDeleteDialog(true);
  };

  const handleApprovalSubmit = (approved: boolean) => {
    setShowApprovalModal(false);
    setApprovalNotes('');
    setApprovalAction(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Cost Code Management</h2>
          <p className="text-muted-foreground">Manage cost codes and track project accounting</p>
        </div>
        <Button onClick={() => handleOpenDialog('add')}>
          <Plus className="h-4 w-4 mr-2" />
          New Cost Code
        </Button>
      </div>

      {/* Enhanced Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Code className="h-4 w-4 text-blue-600" />
              Total Cost Codes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{costCodes.length}</div>
            <p className="text-xs text-blue-600 mt-1">{costCodes.filter(c => c.isActive).length} active codes</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-purple-600" />
              Total Budget
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">{formatCurrency(costCodes.reduce((sum, c) => sum + c.totalBudget, 0), orgCurrency)}</div>
            <p className="text-xs text-purple-600 mt-1">Allocated budget</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-red-600" />
              Actual Spent
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{formatCurrency(costCodes.reduce((sum, c) => sum + c.actualCost, 0), orgCurrency)}</div>
            <p className="text-xs text-red-600 mt-1">Current spending</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Variance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{formatCurrency(costCodes.reduce((sum, c) => sum + c.variance, 0), orgCurrency)}</div>
            <p className="text-xs text-green-600 mt-1">Budget variance</p>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search cost codes by code, name, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 max-w-sm"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="03 - Concrete">03 - Concrete</SelectItem>
            <SelectItem value="05 - Metals">05 - Metals</SelectItem>
            <SelectItem value="06 - Wood & Plastics">06 - Wood & Plastics</SelectItem>
            <SelectItem value="09 - Finishes">09 - Finishes</SelectItem>
            <SelectItem value="15 - Mechanical">15 - Mechanical</SelectItem>
            <SelectItem value="16 - Electrical">16 - Electrical</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending_approval">Pending Approval</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="codes" className="space-y-4">
        <TabsList>
          <TabsTrigger value="codes">Cost Codes</TabsTrigger>
          <TabsTrigger value="analysis">Division Analysis</TabsTrigger>
          <TabsTrigger value="hierarchy">Code Hierarchy</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="codes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-5 w-5" />
                Cost Code Directory
              </CardTitle>
              <CardDescription>Manage cost codes and track budget performance</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Division</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Total Rate</TableHead>
                    <TableHead>Budget</TableHead>
                    <TableHead>Actual</TableHead>
                    <TableHead>Variance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCostCodes.map((code) => (
                    <TableRow key={code.id} className="hover-table-row">
                      <TableCell className="font-mono">{code.code}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getDivisionIcon(code.division)}
                          <span className="text-sm">{code.division}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{code.description}</div>
                          <div className="text-sm text-muted-foreground">{code.category}</div>
                        </div>
                      </TableCell>
                      <TableCell>{code.unit}</TableCell>
                      <TableCell>{formatCurrency(calculateTotalRate(code), orgCurrency)}</TableCell>
                      <TableCell>{formatCurrency(code.totalBudget, orgCurrency)}</TableCell>
                      <TableCell>{formatCurrency(code.actualCost, orgCurrency)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {formatCurrency(Math.abs(code.variance), orgCurrency)}
                          {getVarianceBadge(code.variance)}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(code.isActive)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenDialog('view', code)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenDialog('edit', code)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Code
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <FileText className="h-4 w-4 mr-2" />
                              Cost Report
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>
                              <Archive className="h-4 w-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(code)} className="text-red-600">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analysis" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Division Analysis
              </CardTitle>
              <CardDescription>Cost performance by construction division</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Division</TableHead>
                    <TableHead>Total Budget</TableHead>
                    <TableHead>Actual Cost</TableHead>
                    <TableHead>Committed</TableHead>
                    <TableHead>Variance</TableHead>
                    <TableHead>% Complete</TableHead>
                    <TableHead>Code Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {divisionAnalysis.map((analysis, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{analysis.division}</TableCell>
                      <TableCell>{formatCurrency(analysis.totalBudget, orgCurrency)}</TableCell>
                      <TableCell>{formatCurrency(analysis.actualCost, orgCurrency)}</TableCell>
                      <TableCell>{formatCurrency(analysis.committed, orgCurrency)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className={analysis.variance >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatCurrency(Math.abs(analysis.variance), orgCurrency)}
                          </span>
                          {analysis.variance >= 0 ? 
                            <TrendingUp className="h-4 w-4 text-green-600" /> : 
                            <AlertCircle className="h-4 w-4 text-red-600" />
                          }
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className={analysis.percentComplete > 100 ? 'text-red-600' : 'text-green-600'}>
                            {analysis.percentComplete.toFixed(1)}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{analysis.codes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hierarchy" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderTree className="h-5 w-5" />
                Cost Code Hierarchy
              </CardTitle>
              <CardDescription>Hierarchical view of cost code structure</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {['03 - Concrete', '05 - Metals', '06 - Wood & Plastics', '15 - Mechanical', '16 - Electrical', '09 - Finishes'].map((division) => (
                  <div key={division} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium flex items-center gap-2">
                        {getDivisionIcon(division)}
                        {division}
                      </h4>
                      <Badge variant="outline">1 code</Badge>
                    </div>
                    <div className="ml-6 space-y-2">
                      {costCodes.filter(code => code.division === division).map((code) => (
                        <div key={code.id} className="flex items-center justify-between p-2 bg-muted rounded">
                          <div className="flex items-center gap-2">
                            <Hash className="h-4 w-4" />
                            <span className="font-mono text-sm">{code.code}</span>
                            <span className="text-sm">{code.description}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{formatCurrency(code.totalBudget, orgCurrency)}</span>
                            {getVarianceBadge(code.variance)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Cost Code Reports
              </CardTitle>
              <CardDescription>Generate cost code and budget analysis reports</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold">$942,100</div>
                    <div className="text-sm text-muted-foreground">Total Project Budget</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold">$883,550</div>
                    <div className="text-sm text-muted-foreground">Actual Costs</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold">$20,250</div>
                    <div className="text-sm text-muted-foreground">Under Budget</div>
                  </CardContent>
                </Card>
              </div>
              <div className="flex gap-2">
                <Button variant="outline">Export Cost Summary</Button>
                <Button variant="outline">Export Division Analysis</Button>
                <Button variant="outline">Export Budget vs Actual</Button>
                <Button variant="outline">Export Variance Report</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Cost Code Settings
              </CardTitle>
              <CardDescription>Configure cost code structure and defaults</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Default Code Format</Label>
                    <Select defaultValue="5digit">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3digit">3-Digit (XXX)</SelectItem>
                        <SelectItem value="5digit">5-Digit (XXXXX)</SelectItem>
                        <SelectItem value="custom">Custom Format</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Cost Code Prefix</Label>
                    <Input defaultValue="CC-" />
                  </div>
                  <div>
                    <Label>Maximum Hierarchy Levels</Label>
                    <Input type="number" defaultValue="4" />
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Auto-Calculate Variance</Label>
                    <Select defaultValue="enabled">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="enabled">Enabled</SelectItem>
                        <SelectItem value="disabled">Disabled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Default Labor Rate Markup</Label>
                    <Input defaultValue="15%" />
                  </div>
                  <div>
                    <Label>Variance Alert Threshold</Label>
                    <Input defaultValue="10%" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto modal-content">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'add' ? 'New Cost Code' : 
               dialogMode === 'edit' ? 'Edit Cost Code' : 'View Cost Code'}
            </DialogTitle>
            <DialogDescription>
              {dialogMode === 'add' ? 'Create a new cost code' : 
               dialogMode === 'edit' ? 'Modify cost code details' : 'View cost code information'}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="rates">Rates</TabsTrigger>
              <TabsTrigger value="budget">Budget & Actual</TabsTrigger>
              <TabsTrigger value="hierarchy">Hierarchy</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cost Code</Label>
                  <Input 
                    value={formData.code || ''}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    disabled={dialogMode === 'view'}
                    placeholder="e.g., 03000"
                  />
                </div>
                <div>
                  <Label>Division</Label>
                  <Select 
                    value={formData.division || ''} 
                    onValueChange={(value) => setFormData({...formData, division: value})}
                    disabled={dialogMode === 'view'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select division" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="03 - Concrete">03 - Concrete</SelectItem>
                      <SelectItem value="05 - Metals">05 - Metals</SelectItem>
                      <SelectItem value="06 - Wood & Plastics">06 - Wood & Plastics</SelectItem>
                      <SelectItem value="09 - Finishes">09 - Finishes</SelectItem>
                      <SelectItem value="15 - Mechanical">15 - Mechanical</SelectItem>
                      <SelectItem value="16 - Electrical">16 - Electrical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Input 
                    value={formData.category || ''}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    disabled={dialogMode === 'view'}
                    placeholder="e.g., Structural"
                  />
                </div>
                <div>
                  <Label>Unit of Measure</Label>
                  <Select 
                    value={formData.unit || ''} 
                    onValueChange={(value) => setFormData({...formData, unit: value})}
                    disabled={dialogMode === 'view'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SF">SF - Square Feet</SelectItem>
                      <SelectItem value="LF">LF - Linear Feet</SelectItem>
                      <SelectItem value="CY">CY - Cubic Yards</SelectItem>
                      <SelectItem value="LB">LB - Pounds</SelectItem>
                      <SelectItem value="BF">BF - Board Feet</SelectItem>
                      <SelectItem value="EA">EA - Each</SelectItem>
                      <SelectItem value="LS">LS - Lump Sum</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Input 
                    value={formData.description || ''}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    disabled={dialogMode === 'view'}
                    placeholder="Detailed description of work"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="rates" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Labor Rate (${formData.unit || 'unit'})</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.laborRate || 0}
                    onChange={(e) => setFormData({...formData, laborRate: parseFloat(e.target.value) || 0})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Material Rate (${formData.unit || 'unit'})</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.materialRate || 0}
                    onChange={(e) => setFormData({...formData, materialRate: parseFloat(e.target.value) || 0})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Equipment Rate (${formData.unit || 'unit'})</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.equipmentRate || 0}
                    onChange={(e) => setFormData({...formData, equipmentRate: parseFloat(e.target.value) || 0})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Total Rate (${formData.unit || 'unit'})</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={((formData.laborRate || 0) + (formData.materialRate || 0) + (formData.equipmentRate || 0)).toFixed(2)}
                    disabled
                  />
                </div>
              </div>
              {formData.laborRate !== undefined && formData.materialRate !== undefined && formData.equipmentRate !== undefined && (
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-sm font-medium">Rate Breakdown</div>
                  <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
                    <div>Labor: {((formData.laborRate || 0) / ((formData.laborRate || 0) + (formData.materialRate || 0) + (formData.equipmentRate || 0)) * 100).toFixed(1)}%</div>
                    <div>Material: {((formData.materialRate || 0) / ((formData.laborRate || 0) + (formData.materialRate || 0) + (formData.equipmentRate || 0)) * 100).toFixed(1)}%</div>
                    <div>Equipment: {((formData.equipmentRate || 0) / ((formData.laborRate || 0) + (formData.materialRate || 0) + (formData.equipmentRate || 0)) * 100).toFixed(1)}%</div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="budget" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Total Budget</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.totalBudget || 0}
                    onChange={(e) => setFormData({...formData, totalBudget: parseFloat(e.target.value) || 0})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Actual Cost</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.actualCost || 0}
                    onChange={(e) => setFormData({...formData, actualCost: parseFloat(e.target.value) || 0})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Committed Amount</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.committed || 0}
                    onChange={(e) => setFormData({...formData, committed: parseFloat(e.target.value) || 0})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Variance</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={((formData.totalBudget || 0) - (formData.actualCost || 0) - (formData.committed || 0)).toFixed(2)}
                    disabled
                  />
                </div>
              </div>
              {formData.totalBudget !== undefined && formData.actualCost !== undefined && formData.committed !== undefined && (
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-sm font-medium">Budget Analysis</div>
                  <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                    <div>Spent: {((formData.actualCost || 0) / (formData.totalBudget || 1) * 100).toFixed(1)}%</div>
                    <div>Committed: {((formData.committed || 0) / (formData.totalBudget || 1) * 100).toFixed(1)}%</div>
                    <div>Remaining: {(((formData.totalBudget || 0) - (formData.actualCost || 0) - (formData.committed || 0)) / (formData.totalBudget || 1) * 100).toFixed(1)}%</div>
                    <div className="font-bold">Total Used: {(((formData.actualCost || 0) + (formData.committed || 0)) / (formData.totalBudget || 1) * 100).toFixed(1)}%</div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="hierarchy" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Parent Code</Label>
                  <Select 
                    value={formData.parentCode || ''} 
                    onValueChange={(value) => setFormData({...formData, parentCode: value})}
                    disabled={dialogMode === 'view'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select parent code" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">None (Top Level)</SelectItem>
                      <SelectItem value="03000">03000 - Cast-in-Place Concrete</SelectItem>
                      <SelectItem value="05120">05120 - Structural Steel Framing</SelectItem>
                      <SelectItem value="06100">06100 - Wood Framing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Hierarchy Level</Label>
                  <Input 
                    type="number"
                    value={formData.level || 1}
                    onChange={(e) => setFormData({...formData, level: parseInt(e.target.value) || 1})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Created By</Label>
                  <Input 
                    value={formData.createdBy || ''}
                    onChange={(e) => setFormData({...formData, createdBy: e.target.value})}
                    disabled={dialogMode === 'view'}
                  />
                </div>
                <div>
                  <Label>Active Status</Label>
                  <Select 
                    value={formData.isActive ? 'true' : 'false'} 
                    onValueChange={(value) => setFormData({...formData, isActive: value === 'true'})}
                    disabled={dialogMode === 'view'}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="notes" className="space-y-4">
              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  disabled={dialogMode === 'view'}
                  placeholder="Additional notes or comments about this cost code"
                  rows={6}
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            {dialogMode !== 'view' && (
              <Button onClick={handleSaveCostCode} disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : dialogMode === 'add' ? 'Create Cost Code' : 'Save Changes'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="modal-content">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Cost Code</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete cost code {selectedCostCode?.code}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleConfirmDelete}>
              Delete Cost Code
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}